import React, { useState, useEffect, useContext, useCallback } from 'react';
import ReactModal from 'react-modal';
import { BoxModal } from '../../boxModal';
import { CiCircleInfo } from 'react-icons/ci';
import './cartao-RequestCard-modal.css';
import { getAddressByCep } from '../../../utils/getAddressByCep';
import { AuthContext } from '../../../contexts/AuthContext';
import { birthState } from '../../../utils/birthState';
import { toast } from 'react-toastify';
import { InputField } from './inputField';
import { useCards } from '../../../hooks/useCards';

export const ModalRequestCard = ({ isOpen, onClose, cardType }) => {
  const { user } = useContext(AuthContext);
  const { requestCard } = useCards();
  const [formData, setFormData] = useState({
    address: '',
    addressNumber: '',
    addressComplement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipCode: '',
    cardType: cardType,
    isDependent: false,
    dependentName: '',
    isSecondCopy: false,
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isCepValid, setIsCepValid] = useState(false);
  const [profileFetched, setProfileFetched] = useState(false);
  const [isCepBeingTyped, setIsCepBeingTyped] = useState(false);
  const resetForm = useCallback(() => {
    setFormData({
      address: '',
      addressNumber: '',
      addressComplement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: '',
      cardType: cardType,
      isDependent: false,
      dependentName: '',
      isSecondCopy: false,
    });
    setError('');
    setIsCepValid(false);
    setIsCepBeingTyped(false);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setProfileFetched(false);
    }
  }, [isOpen, resetForm]);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (profileFetched || !user) return;
      try {
        if (user && user.address && user.address.length > 0) {
          const address = user.address[0];
          setFormData((prev) => ({
            ...prev,
            zipCode: address.zipCode,
            address: address.addressLine,
            addressNumber: address.addressLine2,
            addressComplement: address.complement,
            neighborhood: address.neighborhood,
            city: address.cityName,
            state: address.state,
          }));
          if (address.zipCode.length === 8) {
            await handleCepBlur(address.zipCode);
          }
        } else {
          setError('Endereço não encontrado no perfil.');
        }
        setProfileFetched(true);
      } catch (error) {
        console.error('Erro ao buscar perfil:', error);
      }
    };
    if (isOpen) {
      fetchProfileData();
    }
  }, [isOpen, user, profileFetched]);

  const handleInputChange = (field, value) => {
    if (error) setError('');

    if (field === 'isDependent') {
      setFormData((prev) => ({
        ...prev,
        [field]: value === 'true',
        dependentName: '',
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCepChange = async (e) => {
    const cepValue = e.target.value.replace(/[^0-9]/g, '');
    setFormData((prev) => ({ ...prev, zipCode: cepValue }));
    setIsCepBeingTyped(true);
    setIsCepValid(false);
    if (error) setError('');
    if (cepValue.length === 8) {
      await handleCepBlur(cepValue);
    }
  };

  const handleCepBlur = useCallback(async (cep) => {
    if (cep.length === 8) {
      try {
        const address = await getAddressByCep(cep);
        if (address && !address.erro) {
          const stateCode = address.uf;
          const stateIndex = birthState.findIndex(
            (state) => state.label === stateCode
          );
          setFormData((prev) => ({
            ...prev,
            address: address.logradouro || '',
            neighborhood: address.bairro || '',
            city: address.localidade || '',
            state: stateIndex !== -1 ? birthState[stateIndex].label : '',
          }));
          setIsCepValid(true);
        } else {
          setError('CEP não encontrado.');
        }
      } catch (err) {
        setError('Erro ao buscar o CEP.');
      } finally {
        setIsCepBeingTyped(false);
      }
    } else {
      setError('Digite um CEP válido.');
      setIsCepBeingTyped(false);
    }
  }, []);

  const handleSubmit = async () => {
    if (loading) return;

    if (!isCepValid) {
      setError('CEP inválido.');
      return;
    }

    try {
      setLoading(true);
      await requestCard({ ...formData });
      toast.success('Cartão solicitado com sucesso!');
      onClose();
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Erro ao solicitar cartão');
    } finally {
      setLoading(false);
    }
  }

  const isFormDisabled = isCepBeingTyped || !isCepValid;

  const inputFields = [
    {
      name: 'zipCode',
      label: 'CEP*',
      placeholder: 'Digite o CEP (ex: 12345678)',
      maxLength: 8,
      pattern: '[0-9]{8}',
      onChange: handleCepChange,
      onBlur: () => handleCepBlur(formData.zipCode),
    },
    {
      name: 'address',
      label: 'Logradouro*',
      disabled: isFormDisabled,
      onChange: (e) => handleInputChange('address', e.target.value),
    },
    {
      name: 'addressNumber',
      label: 'Número*',
      numericOnly: true,
      disabled: isFormDisabled,
      onChange: (e) => handleInputChange('addressNumber', e.target.value),
    },
    {
      name: 'addressComplement',
      label: 'Complemento',
      disabled: isFormDisabled,
      onChange: (e) => handleInputChange('addressComplement', e.target.value),
    },
    {
      name: 'neighborhood',
      label: 'Bairro*',
      disabled: isFormDisabled,
      onChange: (e) => handleInputChange('neighborhood', e.target.value),
    },
    {
      name: 'city',
      label: 'Cidade*',
      disabled: isFormDisabled,
      onChange: (e) => handleInputChange('city', e.target.value),
    },
  ];

  return (
    <ReactModal
      isOpen={isOpen}
      className="change-modal"
      overlayClassName="overlay-change-password-modal"
    >
      <BoxModal maxWidth={500} title="Solicitar Cartão" close={onClose}>
        <div className="request-card-form">
          <span className="request-card-form-title">Endereço do solicitante</span>
          <div className="d-flex flex-column gap-2 mt-2">
            {inputFields.map((field) => (
              <InputField
                key={field.name}
                label={field.label}
                value={formData[field.name]}
                maxLength={field.maxLength}
                pattern={field.pattern}
                onBlur={field.onBlur}
                placeholder={field.placeholder}
                className={error && !formData[field.name] && field.label.includes('*') ? 'error' : ''}
                onChange={field.onChange}
                disabled={field.disabled}
              />
            ))}

            <div>
              <label>
                Estado*
                <select
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className={error && !formData.state ? 'error' : ''}
                  disabled={isFormDisabled}
                >
                  <option value="">Selecione o Estado</option>
                  {birthState.map((state) => (
                    <option key={state.label} value={state.label}>
                      {state.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div>
              <label>Solicitar para:</label>
              <select
                value={formData.isDependent}
                onChange={(e) => handleInputChange('isDependent', e.target.value)}
              >
                <option value={true}>Dependente</option>
              </select>
            </div>

            <InputField
              label="Nome do Dependente"
              value={formData.dependentName}
              onChange={(e) => handleInputChange('dependentName', e.target.value)}
              className={error && !formData.dependentName ? 'error' : ''}
              disabled={isFormDisabled}
            />

            {error && (
              <div className="error-message">
                <CiCircleInfo className="error-icon" />
                <strong>
                  <span>{error}</span>
                </strong>
              </div>
            )}
          </div>

          <div className="modal-buttons-requestCard">
            <button className="btn btn-outline-secondary" onClick={onClose}>Cancelar</button>
            <button className="btn btn-success" onClick={handleSubmit} disabled={loading}>{loading ? 'Solicitando...' : 'Solicitar Cartão'}</button>
          </div>
        </div>
      </BoxModal>
    </ReactModal>
  );
};
